import type { ReactNode } from 'react'
import { X } from 'lucide-react'

export type NoticeTone = 'info' | 'warning' | 'critical'

const TONE_CLASSES: Record<NoticeTone, { wrap: string; icon: string }> = {
  info: {
    wrap: 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30',
    icon: 'text-blue-600 dark:text-blue-400',
  },
  warning: {
    wrap: 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30',
    icon: 'text-amber-600 dark:text-amber-400',
  },
  critical: {
    wrap: 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/30',
    icon: 'text-red-600 dark:text-red-400',
  },
}

/**
 * The shell every notice in the strip wears.
 *
 * Only the appearance is shared. Where a notice comes from, how often it is
 * polled and what dismissing it means are all different per source and stay
 * with the source — see NoticeBanner.
 */
export function NoticeItem({
  tone,
  icon,
  eyebrow,
  title,
  children,
  footer,
  onDismiss,
}: {
  tone: NoticeTone
  /** Rendered at 16px in the tone's colour; the caller picks the glyph. */
  icon: (className: string) => ReactNode
  /** A short origin marker, such as the platform's name. */
  eyebrow?: string
  title: string
  children: ReactNode
  footer?: ReactNode
  /** Omitted entirely when a notice cannot be closed — never a button that refuses. */
  onDismiss?: () => void
}): React.ReactElement {
  const classes = TONE_CLASSES[tone] ?? TONE_CLASSES.info

  return (
    <div
      className={`relative flex items-start gap-3 rounded-lg border px-4 py-3 shadow-sm ${classes.wrap}`}
      role={tone === 'critical' ? 'alert' : undefined}
    >
      <div className="mt-0.5 flex-shrink-0">{icon(`h-4 w-4 ${classes.icon}`)}</div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {eyebrow && (
            <span className={`mr-1.5 text-xs font-medium uppercase ${classes.icon}`}>
              {eyebrow}
            </span>
          )}
          {title}
        </p>
        <div className="mt-0.5 text-sm text-gray-700 dark:text-gray-300">{children}</div>
        {footer && <div className="mt-1 flex items-center gap-3">{footer}</div>}
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="flex-shrink-0 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
          aria-label="Dismiss notice"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}
